import { useState, type ReactNode } from 'react';
import {
  abrirRevisao, devolverCampanha, disponibilizarRelatorio, emitirCampanha, enviarParaVerificacao,
  verificarCampanha, type AcaoDoRelatorio, type AssinanteDoRelatorio, type RelatorioCompleto,
  type RelatorioDaCampanha,
} from '../../api/cptu';
import { data, dataHora } from '../../formato';
import { AcaoBloqueada, Area, Dialogo, ErroDaApi } from '../../ui';
import { AcaoSecundariaBloqueada } from './CampanhaMoldura';
import { FormularioDaJanela } from './FormularioDaJanela';

/**
 * O fluxo do relatório CPTu na tela — onda 315, Tarefa 315.5 (R1, R3, R4 e R8; doc 296 §7).
 *
 * Duas peças, as duas lidas do `RelatorioCompleto` (a API diz o que cada pessoa pode fazer agora e por quê — a tela
 * não decide nada, só mostra):
 *
 *  · `FaixaDoFluxo` — Elaboração · Verificação · Aprovação do RT · No Portal, cada passo com quem e quando (data e
 *    hora no fuso de Brasília), a devolução com o motivo, e o que falta para emitir (`fn_cptu_falta_para_emitir`, a
 *    mesma lista que o banco cobra);
 *  · `AcoesDoFluxo` — o primário da situação (P-UI-10) e as secundárias: rascunho ou devolvida, «Enviar para
 *    verificação»; em verificação, «Verificar» (e, verificada, «Aprovar e emitir») com «Devolver» ao lado; aprovada
 *    (a emissão que caiu no meio), «Emitir»; emitida, «Disponibilizar no Portal» e «Abrir revisão». A ação que a
 *    pessoa não pode fazer fica à vista, bloqueada, com a frase da API (P-UI-24) — a do §15 para quem elaborou, a da
 *    RN-227 para quem não pode ser RT, a do que falta. Devolver e abrir revisão pedem o motivo numa janela da casa
 *    (`Dialogo`), nunca no `confirm` do navegador. As recusas (409) aparecem com a frase do banco, ao lado das ações.
 */

/** «R00», «R01». */
export const revisao = (n: number) => `R${String(n).padStart(2, '0')}`;

/** A revisão nova aberta sobre a emitida (R8): a campanha voltou ao rascunho, e a corrente ainda é a que vale. */
export function revisaoAberta(r: RelatorioCompleto): boolean {
  return (r.situacao === 'rascunho' || r.situacao === 'devolvida')
    && !!r.relatorio && ['emitido', 'disponibilizado'].includes(r.relatorio.status);
}

/** Quem e quando: «Fulano · 12/10/2026 às 14:05». */
function quemQuando(a: AssinanteDoRelatorio | null): string | null {
  return a ? `${a.nome} · ${dataHora(a.em)}` : null;
}

interface Passo { chave: string; nome: string; estado: 'feito' | 'andamento' | 'pendente' | 'atencao'; texto: ReactNode }

const SIMBOLO = { feito: '✓', andamento: '◐', pendente: '○', atencao: '▲' } as const;
const PALAVRA = { feito: 'feito', andamento: 'em andamento', pendente: 'pendente', atencao: 'atenção' } as const;

function passosDoFluxo(r: RelatorioCompleto): Passo[] {
  const rel = r.relatorio;
  const s = r.situacao;
  const aberta = revisaoAberta(r);
  const enviado = !!rel && !aberta && ['em_verificacao', 'aprovada', 'emitida'].includes(s);
  const devolvido = s === 'devolvida' && !aberta;
  const elaboracao: Passo = enviado || (rel && !aberta && !devolvido)
    ? { chave: 'elaboracao', nome: 'Elaboração', estado: 'feito', texto: quemQuando(rel?.elaborado ?? null) ?? 'enviado' }
    : {
      chave: 'elaboracao', nome: 'Elaboração', estado: 'andamento',
      texto: aberta ? `revisão ${revisao((rel?.revisao ?? 0) + 1)} em elaboração` : devolvido ? 'em correção' : 'em elaboração',
    };
  let verificacao: Passo;
  if (devolvido) {
    verificacao = {
      chave: 'verificacao', nome: 'Verificação', estado: 'atencao',
      texto: `devolvida: ${rel?.motivoDevolucao ?? 'o motivo está no histórico'}`,
    };
  } else if (enviado && rel?.verificado) {
    verificacao = { chave: 'verificacao', nome: 'Verificação', estado: 'feito', texto: `verificado por ${quemQuando(rel.verificado)}` };
  } else if (enviado && s === 'em_verificacao') {
    verificacao = { chave: 'verificacao', nome: 'Verificação', estado: 'andamento', texto: 'aguardando outra pessoa verificar' };
  } else {
    verificacao = { chave: 'verificacao', nome: 'Verificação', estado: 'pendente', texto: 'não enviada' };
  }
  let aprovacao: Passo;
  if (enviado && rel?.aprovado) {
    aprovacao = { chave: 'aprovacao', nome: 'Aprovação do RT', estado: s === 'emitida' ? 'feito' : 'atencao',
      texto: s === 'emitida'
        ? `aprovado por ${quemQuando(rel.aprovado)}${rel.emitido ? ` · emitido em ${dataHora(rel.emitido.em)}` : ''}`
        : `aprovado por ${quemQuando(rel.aprovado)} · a emissão não terminou: «Emitir» tenta de novo` };
  } else if (enviado && rel?.verificado && s === 'em_verificacao') {
    aprovacao = { chave: 'aprovacao', nome: 'Aprovação do RT', estado: 'andamento', texto: 'aguardando o RT' };
  } else {
    aprovacao = { chave: 'aprovacao', nome: 'Aprovação do RT', estado: 'pendente', texto: 'não aprovado' };
  }
  const portal: Passo = rel?.disponibilizadoEm && (enviado || aberta)
    ? { chave: 'portal', nome: 'No Portal', estado: 'feito', texto: `desde ${dataHora(rel.disponibilizadoEm)}` }
    : { chave: 'portal', nome: 'No Portal', estado: 'pendente', texto: 'não publicado' };
  return [elaboracao, verificacao, aprovacao, portal];
}

/**
 * A faixa do fluxo, debaixo do editor (a prancheta «6 · Relatório»): os quatro passos com quem e quando, e o que
 * falta para emitir — a mesma lista que o banco confere na aprovação (R3).
 */
export function FaixaDoFluxo({ r }: { r: RelatorioCompleto }) {
  const passos = passosDoFluxo(r);
  const mostrarFalta = r.situacao !== 'emitida' && r.situacao !== 'cancelada' && r.falta.length > 0;
  return (
    <section className="cptu-fluxo" aria-label="O fluxo do relatório">
      <ol className="cptu-fluxo-passos">
        {passos.map((p) => (
          <li key={p.chave} className={`cptu-fluxo-passo ${p.estado}`} data-passo={p.chave}>
            <span className="cptu-fluxo-nome">{p.nome}</span>
            <span className="cptu-fluxo-texto">
              <span aria-hidden="true">{SIMBOLO[p.estado]} </span>
              <span className="so-leitor">{PALAVRA[p.estado]}: </span>
              {p.texto}
            </span>
          </li>
        ))}
      </ol>
      {mostrarFalta && (
        <div className="cptu-fluxo-falta">
          <strong>Falta para emitir</strong>
          <ul>{r.falta.map((f) => <li key={`${f.codigo}:${f.texto}`}>{f.texto}</li>)}</ul>
        </div>
      )}
      <p className="cptu-fluxo-nota">
        No PDF, o registro eletrônico de quem elaborou, verificou e aprovou, e o QR do relatório interativo.
      </p>
    </section>
  );
}

/* ── As ações do fluxo ─────────────────────────────────────────────── */

type Janela = 'devolver' | 'revisao' | null;

/**
 * As ações do fluxo para a situação de agora, no cabeçalho da aba (a primeira é o primário da tela, P-UI-10; com
 * `secundario`, outro primário está à vista — o «Gravar» de uma seção editada — e todas vão a secundário).
 */
export function AcoesDoFluxo({ campanhaId, codigo, r, aoMudar, secundario = false }: {
  campanhaId: string; codigo: string; r: RelatorioCompleto;
  aoMudar(novo?: RelatorioCompleto): Promise<void>;
  secundario?: boolean;
}) {
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState<AcaoDoRelatorio | null>(null);
  const [janela, setJanela] = useState<Janela>(null);
  const rel = r.relatorio;
  const s = r.situacao;

  const fazer = async (acao: AcaoDoRelatorio, chamada: () => Promise<unknown>) => {
    if (ocupado) return;
    setOcupado(acao); setErro(null);
    try {
      const novo = await chamada();
      await aoMudar(novo && typeof novo === 'object' && 'secoes' in (novo as object) ? novo as RelatorioCompleto : undefined);
    } catch (e) { setErro(e); } finally { setOcupado(null); }
  };

  /** Um botão do fluxo: à vista sempre; bloqueado com o motivo da API quando a pessoa não pode (P-UI-24). */
  const botao = (acao: AcaoDoRelatorio, texto: string, aoClicar: () => void, primario: boolean) => {
    if (!r.pode[acao]) {
      const motivo = r.motivos[acao] ?? 'Esta ação não está disponível agora.';
      // A secundária bloqueada continua secundária: um primário por vez (P-UI-10).
      return primario && !secundario
        ? <AcaoBloqueada key={acao} texto={texto} motivo={motivo} />
        : <AcaoSecundariaBloqueada key={acao} texto={texto} motivo={motivo} />;
    }
    return (
      <button key={acao} type="button" className={primario && !secundario ? 'botao' : 'botao secundario'}
        aria-disabled={!!ocupado} onClick={aoClicar} data-acao={acao}>
        {ocupado === acao ? `${texto}…` : texto}
      </button>
    );
  };

  const acoes: ReactNode[] = [];
  if (s === 'rascunho' || s === 'devolvida') {
    acoes.push(botao('enviar', 'Enviar para verificação', () => { void fazer('enviar', () => enviarParaVerificacao(campanhaId)); }, true));
  } else if (s === 'em_verificacao') {
    if (!rel?.verificado) {
      acoes.push(botao('verificar', 'Verificar', () => { void fazer('verificar', () => verificarCampanha(campanhaId)); }, true));
    } else {
      acoes.push(botao('emitir', 'Aprovar e emitir', () => { void fazer('emitir', () => emitirCampanha(campanhaId)); }, true));
    }
    acoes.push(botao('devolver', 'Devolver', () => setJanela('devolver'), false));
  } else if (s === 'aprovada') {
    acoes.push(botao('emitir', 'Emitir', () => { void fazer('emitir', () => emitirCampanha(campanhaId)); }, true));
  } else if (s === 'emitida' && rel) {
    acoes.push(botao('disponibilizar', 'Disponibilizar no Portal',
      () => { void fazer('disponibilizar', () => disponibilizarRelatorio(rel.id)); }, true));
    acoes.push(botao('revisao', 'Abrir revisão', () => setJanela('revisao'), false));
  }
  if (acoes.length === 0 && !erro) return null;

  return (
    <div className="cptu-acoes-fluxo">
      <div className="cptu-acoes-fluxo-botoes">{acoes}</div>
      {erro ? <div className="cptu-acoes-fluxo-erro"><ErroDaApi erro={erro} /></div> : null}
      {janela === 'devolver' && (
        <JanelaDeMotivo titulo="Devolver para correção" rotulo={`${codigo} · ${rel?.numero ?? ''}`}
          frase="A campanha volta para quem elaborou corrigir: a verificação feita se apaga, e o reenvio mantém o número do relatório."
          botao="Devolver" perigo
          aoFechar={() => setJanela(null)}
          aoGravar={async (motivo) => {
            const novo = await devolverCampanha(campanhaId, motivo);
            setJanela(null);
            await aoMudar(novo);
          }} />
      )}
      {janela === 'revisao' && rel && (
        <JanelaDeMotivo titulo="Abrir revisão" rotulo={`${rel.numero} · ${revisao(rel.revisao)}`}
          frase={`A campanha volta ao rascunho para a revisão ${revisao(rel.revisao + 1)}. A ${revisao(rel.revisao)} continua `
            + `valendo (e no Portal) até a ${revisao(rel.revisao + 1)} ser emitida; o número do relatório se mantém.`}
          botao="Abrir revisão"
          aoFechar={() => setJanela(null)}
          aoGravar={async (motivo) => {
            const novo = await abrirRevisao(campanhaId, motivo);
            setJanela(null);
            await aoMudar(novo);
          }} />
      )}
    </div>
  );
}

/** A janela que pede o motivo (devolver, abrir revisão): 10 caracteres ou mais, que vão à trilha (P-TXT-10). */
function JanelaDeMotivo({ titulo, rotulo, frase, botao, perigo = false, aoFechar, aoGravar }: {
  titulo: string; rotulo: string; frase: string; botao: string; perigo?: boolean;
  aoFechar(): void; aoGravar(motivo: string): Promise<void>;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const falta = motivo.trim().length < 10 ? 'Diga o motivo (pelo menos 10 caracteres).' : null;
  const gravar = async () => {
    if (falta || ocupado) return;
    setOcupado(true); setErro(null);
    try { await aoGravar(motivo.trim()); } catch (e) { setErro(e); setOcupado(false); }
  };
  return (
    <Dialogo aberto titulo={titulo} rotulo={rotulo} aoFechar={aoFechar}>
      <FormularioDaJanela aoEnviar={() => { void gravar(); }}>
        <ErroDaApi erro={erro} />
        <p className="sub">{frase}</p>
        <Area rotulo="Motivo" rows={3} value={motivo} maxLength={2000}
          dica="Pelo menos 10 caracteres: o motivo vai para a trilha." onChange={(e) => setMotivo(e.target.value)} />
        {falta && <p className="cptu-falta" role="status">{falta}</p>}
        <div className="rodape-dialogo">
          <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
          <button type="submit" className={perigo ? 'botao perigo' : 'botao'} aria-disabled={!!falta || ocupado}>
            {ocupado ? `${botao}…` : botao}
          </button>
        </div>
      </FormularioDaJanela>
    </Dialogo>
  );
}

/** Uma revisão na lista do fim da aba: emitida em …, substituída em …, com o PDF e a planilha dela. */
export function textoDaRevisao(x: RelatorioDaCampanha): string {
  const partes = [`${revisao(x.revisao)}`];
  if (x.emitido) partes.push(`emitida em ${data(x.emitido.em)}`);
  else partes.push(x.status === 'aprovado' ? 'aprovada, a emitir' : 'em elaboração');
  if (x.disponibilizadoEm) partes.push(`no Portal desde ${data(x.disponibilizadoEm)}`);
  if (x.substituidoEm) partes.push(`substituída em ${data(x.substituidoEm)}`);
  return partes.join(' · ');
}
